import {
    Badge, Box, Button, Flex, FormControl, FormLabel, Grid, GridItem, IconButton, Image, Input, Select, Spinner, Switch, Text, Textarea, Checkbox, Radio, RadioGroup, Stack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CustomToast, Dropdown, FormInput } from "../../../../components";
import AddIcon from "../../../../assets/Images/Plus-icon.svg"
import { Controller, useForm } from "react-hook-form";
import { useFetch } from "../../../../hooks/useFetch";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { showDate, showTime } from "../../../../utilities/helper";
import { EditIcon, CopyIcon } from "@chakra-ui/icons";
import MyDiv from "./meetingFormV2.style";
import { refreshHistory } from "../../../../components/History/modal";

const getStatusBadge = (status: StatusType, name?: string) => {
    const label = name || StatusType[status] || "Unknown";
    switch (status) {
        case StatusType.Scheduled:
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#EFF8FF" color="#175CD3" border="1px solid #B2DDFF" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
        case StatusType.Done:
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#ECFDF3" color="#027A48" border="1px solid #ABEFC6" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
        case StatusType.Cancelled:
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#FEF3F2" color="#B42318" border="1px solid #FECDCA" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
        case StatusType.Rescheduled:
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#FFFAEB" color="#B54708" border="1px solid #FEDF89" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
        default:
            return <Badge px={2.5} py={0.5} borderRadius="full" bg="#F2F4F7" color="#344054" border="1px solid #D0D5DD" textTransform="none" fontSize="xs" fontWeight="500">{label}</Badge>;
    }
};

interface Props {
    leadId: number;
    viewType?: "table" | "card";
    pocEmail?: string;
    onMeetingSaved?: (leadId: number) => void;
    selectedMeetingId?: number;
}

export enum StatusType {
    Scheduled = 1,
    Done = 2,
    Cancelled = 3,
    Rescheduled = 4,
}

const statusTypeOptions = [
    { id: StatusType.Scheduled, name: "Scheduled" },
    { id: StatusType.Done, name: "Done" },
    { id: StatusType.Cancelled, name: "Cancelled" },
    { id: StatusType.Rescheduled, name: "Rescheduled" },
];

const meetingDurationOptions = [
    { id: 15, name: "15 Minutes" },
    { id: 30, name: "30 Minutes" },
    { id: 45, name: "45 Minutes" },
    { id: 60, name: "60 Minutes" },
    { id: 75, name: "75 Minutes" },
    { id: 90, name: "90 Minutes" },
    { id: 105, name: "105 Minutes" },
    { id: 120, name: "120 Minutes" },
    { id: 135, name: "135 Minutes" },
    { id: 150, name: "150 Minutes" },
    { id: 165, name: "165 Minutes" },
    { id: 180, name: "180 Minutes" },
];

interface IMeeting {
    id: number;
    leadId: number;
    meetingTitle: string;
    description: string;
    meetingDate: string;
    startDateTime: string;
    endDateTime: string;
    meetingDuration: number;
    isAllDay: boolean;
    meetingStatusType: StatusType;
    remarks: string;
    createdAt: string;
    extraEmails: string[];
    createdByName: string;
    meetingStatusName?: string;
    meetingLinkType?: "googleLink" | "usingLink" | "locationVisit" | "LocationVisit" | string;
    meeting_Link?: string;
    locationId?: number;
    locationName?: string;
    locationMaster?: { id: number; locationName: string };
}

interface IMeetingForm {
    meetingTitle: string;
    description: string;
    meetingDate: string;
    startDateTime?: string;
    endDateTime?: string;
    meetingDuration: number;
    isAllDay: boolean;
    meetingStatusType?: StatusType;
    remarks?: string;
    meetingLinkType?: string;
    meeting_Link?: string;
    locationId?: number;
}

const defaultValues: IMeetingForm = {
    meetingTitle: "",
    description: "",
    meetingDate: "",
    startDateTime: "",
    endDateTime: "",
    meetingDuration: 15,
    isAllDay: false,
    meetingStatusType: StatusType.Scheduled,
    remarks: "",
    meetingLinkType: "googleLink",
    meeting_Link: "",
    locationId: undefined,
};

const meetingSchema: yup.ObjectSchema<IMeetingForm> = yup.object({
    meetingTitle: yup.string().required("Title is required").test("no-whitespace", "Title cannot be Empty", value => (value?.trim().length ?? 0) > 0),
    description: yup.string().required("Description is required").test("not-empty", "Description cannot be empty", (value) => {
        if (!value) return false;
        const plainText = value
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, "")
            .trim();
        return plainText.length > 0;
    }),
    meetingDate: yup.string().required("Date is required"),
    isAllDay: yup.boolean().required(),
    startDateTime: yup.string().when("isAllDay", { is: false, then: (schema) => schema.required("Start Time is required"), otherwise: (schema) => schema.default(""), }),
    endDateTime: yup.string().when("isAllDay", {
        is: false, then: (schema) => schema.required("End Time is required"), otherwise: (schema) => schema.default(""),
    }),
    meetingDuration: yup.number().default(0),
    meetingStatusType: yup.mixed<StatusType>().default(StatusType.Scheduled),
    remarks: yup.string().default(""),
    locationId: yup.number().transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined || isNaN(Number(originalValue))
            ? undefined
            : Number(originalValue)
    ).when("meetingLinkType", {
        is: (val: any) => val === "locationVisit" || val === "LocationVisit",
        then: (schema) => schema.typeError("Location is required").required("Location is required").min(1, "Location is required"),
        otherwise: (schema) => schema.nullable().optional(),
    }),
    meetingLinkType: yup
        .mixed<"googleLink" | "usingLink" | "locationVisit">()
        .oneOf(["googleLink", "usingLink", "locationVisit"])
        .required()
        .default("googleLink"),
    meeting_Link: yup.string().when(["meetingLinkType", "isAllDay"], {
        is: (meetingLinkType: string, isAllDay: boolean) => meetingLinkType === "usingLink" && !isAllDay,
        then: (schema) => schema.required("Meeting link is required").url("Enter a valid URL"),
        otherwise: (schema) => schema.url("Enter a valid URL").nullable().transform((v) => v === "" ? null : v),
    }),
});

const MeetingFormTabV2 = ({ leadId, onMeetingSaved, pocEmail: propPocEmail, selectedMeetingId }: Props) => {
    const [showForm, setShowForm] = useState(true);
    const [editId, setEditId] = useState<number | null>(null);
    const { addToast } = CustomToast();
    const [meetings, setMeetings] = useState<IMeeting[]>([]);
    const [hasAutoEdited, setHasAutoEdited] = useState(false);
    const isValidUrl = (url?: string) => {
        if (!url) return false;
        return url.startsWith("http://") || url.startsWith("https://");
    };
    const handleCopyLink = (link: string) => {
        if (!link) return;
        navigator.clipboard.writeText(link);
        addToast({
            message: "Meeting link copied successfully!",
            status: "success",
        });
    };
    const isMeetingLinkActive = (row: IMeeting) => {
        if (row.meetingStatusType !== 1 && row.meetingStatusType !== 4) {
            return false;
        }
        if (!row.meeting_Link || !isValidUrl(row.meeting_Link)) return false;
        const now = new Date();
        if (row.isAllDay) {
            const todayStr = now.toLocaleDateString("en-CA");
            const meetingDateStr = row.meetingDate ? row.meetingDate.split("T")[0] : "";
            return todayStr === meetingDateStr;
        }
        if (!row.startDateTime || !row.endDateTime) return false;
        const start = new Date(row.startDateTime.endsWith("Z") ? row.startDateTime : `${row.startDateTime}Z`);
        const end = new Date(row.endDateTime.endsWith("Z") ? row.endDateTime : `${row.endDateTime}Z`);
        return now >= start && now <= end;
    };
    const { fetchApi, loading } = useFetch(addToast);
    const [filterLoading, setFilterLoading] = useState(false);
    const [meetingFilter, setMeetingFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [emailInput, setEmailInput] = useState("");
    const [emailError, setEmailError] = useState("");
    const [originalStatus, setOriginalStatus] = useState<StatusType | null>(null);
    const [emailList, setEmailList] = useState<string[]>(propPocEmail ? [propPocEmail.toLowerCase()] : []);
    const [pocEmail, setPocEmail] = useState(propPocEmail || "");

    const [includeSignature, setIncludeSignature] = useState(true);
    const [signatureType, setSignatureType] = useState<"Short" | "Full">("Full");
    const [includeDisclaimer, setIncludeDisclaimer] = useState(true);

    const [userSignature, setUserSignature] = useState<{ shortSignature: string; fullSignature: string } | null>(null);
    const [websiteDisclaimer, setWebsiteDisclaimer] = useState<string>("");
    const [loadingSignature, setLoadingSignature] = useState(false);
    const [loadingDisclaimer, setLoadingDisclaimer] = useState(false);
    const [locations, setLocations] = useState<{ id: number; name: string; locationName: string }[]>([]);

    const loadLocations = async () => {
        try {
            const res = await fetchApi("Dropdown/getLocation", "GET");
            const rawList = Array.isArray(res)
                ? res
                : Array.isArray((res as any)?.data)
                ? (res as any).data
                : Array.isArray((res as any)?.locations)
                ? (res as any).locations
                : Array.isArray((res as any)?.result)
                ? (res as any).result
                : [];

            if (rawList && rawList.length > 0) {
                const formatted = rawList.map((x: any) => {
                    const idVal = x.id ?? x.Id ?? x.locationId ?? x.LocationId ?? x.value;
                    const nameVal = x.locationName ?? x.LocationName ?? x.name ?? x.Name ?? x.text ?? x.title ?? "";
                    return {
                        id: Number(idVal),
                        name: String(nameVal),
                        locationName: String(nameVal),
                        locationId: Number(idVal),
                    };
                });
                setLocations(formatted);
            }
        } catch (e) {
            console.error("Error loading locations:", e);
        }
    };

    useEffect(() => {
        loadLocations();
    }, []);

    useEffect(() => {
        const fetchSignature = async () => {
            if (includeSignature && !userSignature && !loadingSignature) {
                setLoadingSignature(true);
                try {
                    const res = await fetchApi<{ shortSignature: string; fullSignature: string }>("UserSignature", "GET");
                    if (res) {
                        setUserSignature({
                            shortSignature: res.shortSignature || "",
                            fullSignature: res.fullSignature || "",
                        });
                    }
                } catch (error) {
                    console.error("Error fetching signature:", error);
                } finally {
                    setLoadingSignature(false);
                }
            }
        };
        fetchSignature();
    }, [includeSignature, userSignature, loadingSignature]);

    useEffect(() => {
        const fetchDisclaimer = async () => {
            if (includeDisclaimer && !websiteDisclaimer && !loadingDisclaimer) {
                setLoadingDisclaimer(true);
                try {
                    const res = await fetchApi<{ disclaimer: string }>("WebsiteSetting", "GET");
                    if (res) {
                        setWebsiteDisclaimer(res.disclaimer || "");
                    }
                } catch (error) {
                    console.error("Error fetching disclaimer:", error);
                } finally {
                    setLoadingDisclaimer(false);
                }
            }
        };
        fetchDisclaimer();
    }, [includeDisclaimer, websiteDisclaimer, loadingDisclaimer]);

    const { control, watch, setValue, handleSubmit, reset, register, formState: { errors }, } = useForm<IMeetingForm>({
        resolver: yupResolver(meetingSchema),
        defaultValues,
    });

    const isValidEmail = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const isAllDay = watch("isAllDay");
    const selectedMeetingLinkType = watch("meetingLinkType");
    const meetingLinkTypeOptions = isAllDay
        ? [
            { id: "usingLink", name: "Using Link" },
            { id: "locationVisit", name: "Location Visit" },
        ]
        : [
            { id: "googleLink", name: "Google Link" },
            { id: "usingLink", name: "Using Link" },
            { id: "locationVisit", name: "Location Visit" },
        ];

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
            if (!trimmed) return;
            if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
            if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
            setEmailList(prev => [...prev, trimmed]);
            setEmailInput("");
            setEmailError("");
        }
    };

    const handleEmailBlur = () => {
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (!trimmed) return;
        if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
        if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
        setEmailList(prev => [...prev, trimmed]);
        setEmailInput("");
        setEmailError("");
    };

    const removeEmail = (email: string) =>
        setEmailList(prev => prev.filter(e => e !== email));

    const meetingFilters = [
        { id: "upcoming", name: "Upcoming" },
        { id: "all", name: "All" },
        { id: "previous", name: "Previous" },
    ];

    const statusFilters = [
        { id: "all", name: "All" },
        { id: "Scheduled", name: "Scheduled" },
        { id: "Done", name: "Done" },
        { id: "Cancelled", name: "Cancelled" },
        { id: "Rescheduled", name: "Rescheduled" },
    ];

    const startTime = watch("startDateTime");
    const today = new Date().toISOString().split("T")[0];

    const getStatusOptions = (currentStatus?: StatusType) => {
        switch (currentStatus) {
            case StatusType.Rescheduled:
                return statusTypeOptions.filter(
                    x => x.id !== StatusType.Scheduled
                );
            case StatusType.Done:
                return [
                    { id: StatusType.Done, name: "Done" }
                ];
            case StatusType.Cancelled:
                return [
                    { id: StatusType.Cancelled, name: "Cancelled" }
                ];
            default:
                return statusTypeOptions;
        }
    };

    const loadMeetings = async () => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setFilterLoading(true);
        const res = await fetchApi(`LeadRequirement/LeadMeeting?leadId=${leadId}&filter=${meetingFilter}&status=${statusFilter}&timeZone=${encodeURIComponent(timezone)}`, "GET");
        if (Array.isArray(res?.data)) {
            setMeetings(res.data);
            setFilterLoading(false);
        } else {
            setMeetings([]);
        }
    };

    useEffect(() => {
        setMeetings([]);
        if (leadId) {
            loadMeetings();
        }
    }, [leadId, meetingFilter, statusFilter]);

    useEffect(() => {
        if (propPocEmail) {
            const email = propPocEmail.toLowerCase();
            setPocEmail(email);
            if (editId === null) {
                setEmailList(prev => prev.includes(email) ? prev : [email]);
            }
        } else if (leadId) {
            const getPocDetails = async () => {
                const res = await fetchApi(`Lead/GetLeadCompanyPocByLeadId/${leadId}`, "GET");
                if (res?.status && res.data?.pocEmail) {
                    const email = res.data.pocEmail.toLowerCase();
                    setPocEmail(email);
                    if (editId === null) {
                        setEmailList(prev => prev.includes(email) ? prev : [email]);
                    }
                }
            };
            getPocDetails();
        }
    }, [leadId, propPocEmail, editId]);

    useEffect(() => {
        if (selectedMeetingId) {
            setMeetingFilter("all");
            setStatusFilter("all");
        }
    }, [selectedMeetingId]);

    useEffect(() => {
        if (selectedMeetingId && meetings.length > 0 && !hasAutoEdited) {
            const match = meetings.find(m => m.id === selectedMeetingId);
            if (match) {
                handleEdit(match);
                setHasAutoEdited(true);
            }
        }
    }, [selectedMeetingId, meetings, hasAutoEdited]);

    const onSubmit = async (data: IMeetingForm) => {
        // if (editId === null && !data.isAllDay && data.startDateTime) {
        //     const selectedStartDateTime = new Date(`${data.meetingDate}T${data.startDateTime}`);
        //     if (selectedStartDateTime.getTime() <= Date.now()) {
        //         addToast({
        //             message: "Meeting start time must be greater than current time.",
        //             status: "warning",
        //         });
        //         return;
        //     }
        // }


        const isReschedule = editId !== null && data.meetingStatusType === StatusType.Rescheduled;

        const shouldValidateTime = !data.isAllDay && data.startDateTime && (editId === null || isReschedule);

        if (shouldValidateTime) {
            const selectedStartDateTime = new Date(`${data.meetingDate}T${data.startDateTime}`
            );

            const now = new Date();

            if (selectedStartDateTime <= now) {
                addToast({
                    message:
                        "Rescheduled meeting must be scheduled for a future date and time.",
                    status: "warning",
                });
                return;
            }
        }
        let finalEmails = [...emailList];
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (trimmed) {
            if (isValidEmail(trimmed)) {
                if (!finalEmails.includes(trimmed)) {
                    finalEmails.push(trimmed);
                    setEmailList(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
                    setEmailInput("");
                    setEmailError("");
                }
            } else {
                setEmailError("Invalid email address.");
                return;
            }
        }

        const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const meetingStatusName = statusTypeOptions.find(x => x.id === data.meetingStatusType)?.name ?? "";
        const payload = {
            leadId, ...data,
            locationId: data.meetingLinkType === "locationVisit" && data.locationId ? Number(data.locationId) : null,
            LocationId: data.meetingLinkType === "locationVisit" && data.locationId ? Number(data.locationId) : null,
            meetingStatusName,
            meetingDuration: Number(data.meetingDuration),
            startDateTime: !data.isAllDay && data.startDateTime ? new Date(`${data.meetingDate}T${data.startDateTime}`).toISOString() : null,
            endDateTime: !data.isAllDay && data.endDateTime
                ? new Date(`${data.meetingDate}T${data.endDateTime}`).toISOString()
                : null,
            extraEmails: finalEmails,
            timeZoneId: timeZoneId,
            meetingLinkType: data.meetingLinkType,
            meeting_Link: data.meetingLinkType === "googleLink" ? "googleLink" : data.meetingLinkType === "locationVisit" ? "" : data.meeting_Link,
            IncludeSignature: includeSignature,
            IncludeDisclaimer: includeDisclaimer,
            SignatureType: signatureType,
        };

        const submitRequest = async (showSuccessMsg: boolean) => {
            const msg = showSuccessMsg ? (editId ? "Meeting Updated Successfully" : "Meeting Added Successfully") : undefined;
            return editId
                ? await fetchApi(`LeadRequirement/LeadMeeting/${editId}`, "PUT", payload, null, msg)
                : await fetchApi("LeadRequirement/LeadMeeting", "POST", payload, null, msg);
        };

        const handleSuccessFlow = () => {
            setEditId(null);
            setOriginalStatus(null);
            setIsReadOnlyMeeting(false);
            setEmailList(pocEmail ? [pocEmail] : []);
            setEmailInput("");
            setIncludeSignature(false);
            setIncludeDisclaimer(false);
            setSignatureType("Short");
            reset(defaultValues);
            loadMeetings();
            onMeetingSaved?.(leadId);
            refreshHistory("Lead", leadId);
        };

        const res = await submitRequest(false);

        if (res && res.requiresAuth && res.authUrl) {
            const width = 500;
            const height = 650;
            const left = window.screen.width / 2 - width / 2;
            const top = window.screen.height / 2 - height / 2;

            const popup = window.open(
                res.authUrl,
                "google-oauth-popup",
                `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes`
            );

            if (popup) {
                const handleOAuthMessage = async (event: MessageEvent) => {
                    if (event.data === "google-connected") {
                        window.removeEventListener("message", handleOAuthMessage);

                        addToast({
                            message: "Google account connected! Saving meeting...",
                            status: "info",
                        });

                        const retryRes = await submitRequest(true);
                        if (retryRes && !retryRes.requiresAuth) {
                            handleSuccessFlow();
                        }
                    }
                };

                window.addEventListener("message", handleOAuthMessage);
            } else {
                addToast({
                    message: "Popup blocked. Please allow popups to connect Google Calendar.",
                    status: "warning",
                });
            }
            return;
        }

        if (res) {
            addToast({
                message: editId ? "Meeting Updated Successfully" : "Meeting Added Successfully",
                status: "success",
            });
            handleSuccessFlow();
        }
    };

    const [isReadOnlyMeeting, setIsReadOnlyMeeting] = useState(false);
    const formatMeetingDate = (date: string) => {
        if (!date) return "-";

        const [year, month, day] = date.split("T")[0].split("-");
        return `${day}-${month}-${year}`;
    };
    const handleEdit = (row: IMeeting) => {
        setOriginalStatus(row.meetingStatusType);
        setIsReadOnlyMeeting(
            row.meetingStatusType === StatusType.Done ||
            row.meetingStatusType === StatusType.Cancelled
        );
        setEditId(row.id!);
        setEmailList(row.extraEmails ?? []);
        reset({
            ...row,
            locationId: row.locationId ?? (row as any).LocationId ?? undefined,
            meetingDate: row.isAllDay ? row.meetingDate?.split("T")[0] || "" : row.startDateTime ? new Date(`${row.startDateTime}Z`).toLocaleDateString("en-CA")
                : "",
            startDateTime: row.isAllDay ? "" : row.startDateTime
                ? new Date(`${row.startDateTime}Z`).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                })
                : "",

            endDateTime: row.isAllDay ? "" : row.endDateTime ? new Date(`${row.endDateTime}Z`).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
                : "",
            remarks: row.remarks || "",
            meetingLinkType: row.meetingLinkType ?? (row.locationId ? "locationVisit" : row.meeting_Link === "googleLink" ? "googleLink" : "usingLink"),
            meeting_Link: row.meetingLinkType === "usingLink" ? row.meeting_Link || "" : (row.meeting_Link === "googleLink" ? "" : row.meeting_Link || ""),
        });
        setShowForm(true);
        // Scroll to the top of the meeting container or form if necessary
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const closeModal = () => {
        setEditId(null);
        setOriginalStatus(null);
        setIsReadOnlyMeeting(false);
        setEmailList(pocEmail ? [pocEmail] : []); setEmailInput(""); setEmailError("");
        setIncludeSignature(false);
        setIncludeDisclaimer(false);
        setSignatureType("Short");
        reset(defaultValues);
    };

    useEffect(() => {
        if (isAllDay) {
            setValue("startDateTime", "");
            setValue("endDateTime", "");
            setValue("meetingDuration", 0);
            if (selectedMeetingLinkType === "googleLink") {
                setValue("meetingLinkType", "usingLink");
            }
        }
    }, [isAllDay, setValue, selectedMeetingLinkType]);

    const meetingDuration = watch("meetingDuration");

    useEffect(() => {
        if (startTime && meetingDuration) {
            const start = new Date(`2000-01-01T${startTime}`);
            start.setMinutes(
                start.getMinutes() + Number(meetingDuration)
            );
            const hh = String(start.getHours()).padStart(2, "0");
            const mm = String(start.getMinutes()).padStart(2, "0");
            setValue("endDateTime", `${hh}:${mm}`);
        }
    }, [startTime, meetingDuration, setValue]);

    const isMeetingExpired = (row: IMeeting) => {
        if (row.isAllDay) {
            if (!row.meetingDate) return false;

            const meetingDate = row.meetingDate.split("T")[0];
            const today = new Date().toLocaleDateString("en-CA");

            return meetingDate < today;
        }

        if (!row.endDateTime) return false;

        const endDateLocal = new Date(`${row.endDateTime}Z`);
        const nowLocal = new Date();

        return endDateLocal < nowLocal;
    };

    const isMeetingApproaching = (row: IMeeting) => {
        if (
            row.meetingStatusType !== StatusType.Scheduled ||
            !row.startDateTime ||
            !row.endDateTime
        ) {
            return false;
        }

        const now = new Date();
        const meetingStart = new Date(`${row.startDateTime}Z`);
        const meetingEnd = new Date(`${row.endDateTime}Z`);

        const diffMinutes =
            (meetingStart.getTime() - now.getTime()) / (1000 * 60);

        return diffMinutes <= 15 && now <= meetingEnd;
    };

    const isMeetingToday = (row: IMeeting) => {
        if (isMeetingExpired(row)) return false;
        if (isMeetingApproaching(row)) return false;

        const today = new Date();

        if (row.isAllDay) {
            const meetingDate = row.meetingDate.split("T")[0];
            const todayDate = today.toLocaleDateString("en-CA");

            return (
                meetingDate === todayDate &&
                row.meetingStatusType === StatusType.Scheduled
            );
        }

        if (!row.startDateTime) return false;

        const meetingDate = new Date(`${row.startDateTime}Z`);

        return (
            meetingDate.getFullYear() === today.getFullYear() &&
            meetingDate.getMonth() === today.getMonth() &&
            meetingDate.getDate() === today.getDate() &&
            row.meetingStatusType === StatusType.Scheduled
        );
    };

    return (
        <MyDiv>
            <Box>
                {/* Header: Title and Filters */}
                <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                    <Text className="font-poppins text_lg text_semibold">
                        Meetings
                    </Text>

                    <Flex gap={3} align="center" wrap="wrap">
                        <Select size="sm" w="120px" bg="white" borderRadius="8px" borderColor="#D0D5DD" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            {statusFilters.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </Select>

                        <Select size="sm" w="120px" bg="white" borderRadius="8px" borderColor="#D0D5DD" value={meetingFilter} onChange={(e) => setMeetingFilter(e.target.value)}>
                            {meetingFilters.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </Select>
                    </Flex>
                </Flex>

                {/* Inline Form */}
                {showForm && (
                    <Box
                        mb={6}
                        p={5}
                        bg="white"
                        border="1px solid #EAECF0"
                        borderRadius="12px"
                        boxShadow="0px 1px 2px rgba(16, 24, 40, 0.05)"
                    >
                        {/* Form Inner Header: Title and All Day Switch */}
                        <Flex justify="space-between" align="center" mb={4} pb={3} borderBottom="1px solid #F2F4F7">
                            <Text fontSize="md" fontWeight="600" color="#101828">
                                {editId ? "Edit Meeting Details" : "Schedule a New Meeting"}
                            </Text>
                            <Controller
                                name="isAllDay"
                                control={control}
                                render={({ field }) => (
                                    <FormControl display="flex" alignItems="center" w="auto" mt={0}>
                                        <FormLabel mb="0" fontSize="sm" fontWeight="500" color="#344054" mr={2}>All Day</FormLabel>
                                        <Switch size="md" isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} isDisabled={isReadOnlyMeeting} colorScheme="green" />
                                    </FormControl>
                                )}
                            />
                        </Flex>

                        <form noValidate onSubmit={handleSubmit(onSubmit)}>
                            <Grid templateColumns="repeat(3,1fr)" gap={3}>
                                {/* Row 1: Title, Email, Date */}
                                <GridItem colSpan={1}>
                                    <FormInput name="meetingTitle" control={control} errors={errors} label="Title" isRequired disable={isReadOnlyMeeting} />
                                </GridItem>

                                <GridItem colSpan={1}>
                                    <FormControl isInvalid={!!emailError} mt={2}>
                                        <FormLabel className="font-poppins text_medium text_md" color="#344054" mb={2}>
                                            Invite via Email
                                        </FormLabel>
                                        {emailList.length > 0 && (
                                            <Flex flexWrap="wrap" gap={1.5} mb={2.5} p={2} border="1px solid" borderColor="#EAECF0" borderRadius="8px" bg="#F9FAFB">
                                                {emailList.map(email => (
                                                    <Flex key={email} align="center" gap={1} bg="#EEF4FF" color="#3538CD" borderRadius="6px" px={2.5} py={0.75} fontSize="xs" fontWeight="500" border="1px solid" borderColor="#C7D7FE">
                                                        <Text>{email}</Text>
                                                        <Text cursor="pointer" fontWeight="bold" ml={1.5} onClick={() => removeEmail(email)} _hover={{ color: "#B93815" }}>×</Text>
                                                    </Flex>
                                                ))}
                                            </Flex>
                                        )}
                                        <Input
                                            placeholder="Type email and press Enter or , to add"
                                            value={emailInput}
                                            onChange={e => { setEmailInput(e.target.value); setEmailError(""); }}
                                            onKeyDown={handleEmailKeyDown}
                                            onBlur={handleEmailBlur}
                                            bg="white"
                                            borderColor="#D0D5DD"
                                            _focus={{ borderColor: "#4048D6", boxShadow: "0 0 0 1px #4048D6" }}
                                            isDisabled={isReadOnlyMeeting}
                                            borderRadius="8px"
                                            size="md"
                                        />
                                        {emailError && <Text color="red.500" fontSize="sm" mt={1}>{emailError}</Text>}
                                    </FormControl>
                                </GridItem>

                                <GridItem colSpan={1}>
                                    <FormInput isRequired name="meetingDate" control={control} errors={errors} label="Date" type="date" min={today} disable={isReadOnlyMeeting} />
                                </GridItem>

                                {/* Row 2: Duration, Start Time, End Time */}
                                <GridItem colSpan={1}>
                                    <Dropdown control={control} name="meetingDuration" label="Duration" options={meetingDurationOptions} valueKey="id" labelKey="name" errors={errors} disable={isReadOnlyMeeting} />
                                </GridItem>

                                <GridItem colSpan={1}>
                                    <FormInput name="startDateTime" control={control} errors={errors} label="Start Time" type="time" disable={isAllDay || isReadOnlyMeeting} />
                                </GridItem>

                                <GridItem colSpan={1}>
                                    <FormInput name="endDateTime" control={control} label="End Time" type="time" disable={true} />
                                </GridItem>

                                {/* Row 3: Meeting Type & Meeting Link / Location */}
                                <GridItem colSpan={selectedMeetingLinkType === "usingLink" || selectedMeetingLinkType === "locationVisit" ? 1 : 3}>
                                    <Dropdown
                                        control={control}
                                        name="meetingLinkType"
                                        label="Meeting Type"
                                        options={meetingLinkTypeOptions}
                                        valueKey="id"
                                        labelKey="name"
                                        errors={errors}
                                        disable={isReadOnlyMeeting}
                                    />
                                </GridItem>

                                {selectedMeetingLinkType === "usingLink" && (
                                    <GridItem colSpan={2}>
                                        <FormInput
                                            name="meeting_Link"
                                            control={control}
                                            errors={errors}
                                            label="Meeting Link"
                                            placeholder="Enter meeting URL"
                                            disable={isReadOnlyMeeting}
                                        />
                                    </GridItem>
                                )}

                                {selectedMeetingLinkType === "locationVisit" && (
                                    <GridItem colSpan={2}>
                                        <Dropdown
                                            isRequired
                                            control={control}
                                            name="locationId"
                                            label="Location"
                                            placeholder="Select Location"
                                            options={locations}
                                            valueKey="id"
                                            labelKey="name"
                                            errors={errors}
                                            disable={isReadOnlyMeeting}
                                        />
                                    </GridItem>
                                )}

                                {editId && watch("meeting_Link") && isValidUrl(watch("meeting_Link")) && (() => {
                                    const currentMeeting = meetings.find(m => m.id === editId);
                                    const active = currentMeeting ? isMeetingLinkActive(currentMeeting) : false;
                                    return (
                                        <GridItem colSpan={3}>
                                            <FormControl>
                                                <FormLabel className="font-poppins text_medium text_md" color="#344054" mb={2}>
                                                    Joining Link {!active && <Text as="span" color="gray.400" fontSize="sm">(Inactive until meeting starts)</Text>}
                                                </FormLabel>
                                                <Flex align="center" gap={3} p={3} bg={active ? "#EEF4FF" : "gray.50"} borderRadius="8px" border="1px solid" borderColor={active ? "#C7D7FE" : "gray.200"}>
                                                    {active ? (
                                                        <Text as="a" href={watch("meeting_Link")} target="_blank" rel="noopener noreferrer" color="#3538CD" fontWeight="600" textDecoration="underline" isTruncated flex="1" fontSize="sm">
                                                            {watch("meeting_Link")}
                                                        </Text>
                                                    ) : (
                                                        <Text color="gray.400" fontWeight="500" cursor="not-allowed" isTruncated flex="1" fontSize="sm" title="Link will be active during the scheduled meeting time">
                                                            {watch("meeting_Link")} (Inactive)
                                                        </Text>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        leftIcon={<CopyIcon />}
                                                        onClick={() => { if (active) handleCopyLink(watch("meeting_Link")!); }}
                                                        isDisabled={!active}
                                                        bg={active ? "#4048D6" : "gray.200"}
                                                        color={active ? "white" : "gray.400"}
                                                        _hover={active ? { bg: "#353CC4" } : undefined}
                                                        borderRadius="8px"
                                                    >
                                                        Copy Link
                                                    </Button>
                                                </Flex>
                                            </FormControl>
                                        </GridItem>
                                    );
                                })()}

                                {/* Row 4: Description */}
                                <GridItem colSpan={3}>
                                    <FormControl isInvalid={!!errors.description} isRequired mt={2}>
                                        <FormLabel className="font-poppins text_medium text_md" color="#344054" mb={2}>
                                            Description
                                        </FormLabel>
                                        <Textarea
                                            placeholder="Add Description"
                                            bg="white"
                                            borderColor="#D0D5DD"
                                            borderRadius="8px"
                                            minH="50px"
                                            height="60px"
                                            {...register("description")}
                                            isDisabled={isReadOnlyMeeting}
                                        />
                                        {errors.description && (
                                            <Text color="red.500" fontSize="sm" mt={1}>
                                                {errors.description.message}
                                            </Text>
                                        )}
                                    </FormControl>
                                </GridItem>

                                <GridItem colSpan={3}>
                                    <FormControl>
                                        <FormLabel>Email Options</FormLabel>

                                        <Flex direction="column" gap={4}>
                                            <Box>
                                                <Checkbox isChecked={includeSignature}
                                                    onChange={(e) => setIncludeSignature(e.target.checked)}
                                                    isDisabled={isReadOnlyMeeting}
                                                >
                                                    Show Signature
                                                </Checkbox>
                                                {includeSignature && (
                                                    <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                        <RadioGroup onChange={(val) => setSignatureType(val as "Short" | "Full")} value={signatureType}>
                                                            <Flex gap={4} mb={2}>
                                                                <Radio value="Short" colorScheme="blue" isDisabled={isReadOnlyMeeting}>Short Signature</Radio>
                                                                <Radio value="Full" colorScheme="blue" isDisabled={isReadOnlyMeeting}>Full Signature</Radio>
                                                            </Flex>
                                                        </RadioGroup>
                                                        {loadingSignature ? (
                                                            <Flex align="center" gap={2} mt={2}>
                                                                <Spinner size="xs" color="#4048D6" />
                                                                <Text fontSize="xs" color="gray.500">Loading signature...</Text>
                                                            </Flex>
                                                        ) : (
                                                            <Box
                                                                mt={2}
                                                                p={3}
                                                                bg="gray.50"
                                                                borderRadius="md"
                                                                border="1px solid"
                                                                borderColor="gray.200"
                                                                maxH="150px"
                                                                overflowY="auto"
                                                                fontSize="sm"
                                                            >
                                                                {signatureType === "Short" ? (
                                                                    userSignature?.shortSignature ? (
                                                                        <Box dangerouslySetInnerHTML={{ __html: userSignature.shortSignature }} />
                                                                    ) : (
                                                                        <Text color="gray.400" fontStyle="italic">No short signature configured.</Text>
                                                                    )
                                                                ) : (
                                                                    userSignature?.fullSignature ? (
                                                                        <Box dangerouslySetInnerHTML={{ __html: userSignature.fullSignature }} />
                                                                    ) : (
                                                                        <Text color="gray.400" fontStyle="italic">No full signature configured.</Text>
                                                                    )
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box>
                                                <Checkbox
                                                    isChecked={includeDisclaimer}
                                                    onChange={(e) => setIncludeDisclaimer(e.target.checked)}
                                                    isDisabled={isReadOnlyMeeting}
                                                >
                                                    Show Disclaimer
                                                </Checkbox>
                                                {includeDisclaimer && (
                                                    <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                        {loadingDisclaimer ? (
                                                            <Flex align="center" gap={2}>
                                                                <Spinner size="xs" color="#4048D6" />
                                                                <Text fontSize="xs" color="gray.500">Loading disclaimer...</Text>
                                                            </Flex>
                                                        ) : (
                                                            <Box
                                                                p={3}
                                                                bg="gray.50"
                                                                borderRadius="md"
                                                                border="1px solid"
                                                                borderColor="gray.200"
                                                                maxH="150px"
                                                                overflowY="auto"
                                                                fontSize="sm"
                                                            >
                                                                {websiteDisclaimer ? (
                                                                    <Box dangerouslySetInnerHTML={{ __html: websiteDisclaimer }} />
                                                                ) : (
                                                                    <Text color="gray.400" fontStyle="italic">No disclaimer configured.</Text>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Flex>
                                    </FormControl>
                                </GridItem>

                                {/* Row 5: Status, Remarks */}
                                <GridItem colSpan={editId ? 1 : 3}>
                                    <Dropdown
                                        control={control}
                                        name="meetingStatusType"
                                        label="Status"
                                        options={getStatusOptions(originalStatus ?? undefined)}
                                        valueKey="id"
                                        labelKey="name"
                                        errors={errors}
                                        disable={originalStatus === StatusType.Done || originalStatus === StatusType.Cancelled}
                                    />
                                </GridItem>

                                {editId && (
                                    <GridItem colSpan={2}>
                                        <FormControl mt={2}>
                                            <FormLabel className="font_poppins text_md text_medium" color="#344054" mb={2}>Remarks</FormLabel>
                                            <Textarea placeholder="Add Remarks" bg="white" borderColor="#D0D5DD" borderRadius="8px" minH="50px" height="60px" className="font_poppins text_regular text_sm" {...register(`remarks`)} isDisabled={isReadOnlyMeeting} />
                                        </FormControl>
                                    </GridItem>
                                )}
                            </Grid>

                            {/* Form Action Buttons */}
                            <Flex justify="flex-end" mt={6} pt={4} borderTop="1px solid #F2F4F7" gap={3}>
                                {editId && (
                                    <Button variant="outline" size="sm" h="36px" borderRadius="8px" borderColor="#D0D5DD" color="#344054" _hover={{ bg: "#F9FAFB" }} onClick={closeModal}>
                                        Cancel
                                    </Button>
                                )}
                                <Button bg="#4048D6" color="white" size="sm" h="36px" borderRadius="8px" _hover={{ bg: "#353CC4" }} type="submit" isLoading={loading} isDisabled={isReadOnlyMeeting}>
                                    {editId ? "Update" : "Save"}
                                </Button>
                            </Flex>
                        </form>
                    </Box>
                )}

                {/* Cards List */}
                <Box>
                    {filterLoading ? (
                        <Flex justify="center" align="center" height="300px">
                            <Spinner thickness="2px" speed="0.65s" color="#0052CC" size="lg" />
                        </Flex>
                    ) : meetings.length === 0 ? (
                        <Flex direction="column" justify="center" align="center" height="300px">
                            <Text color="gray.500">No Meetings Found</Text>
                        </Flex>
                    ) : (
                        <Grid gap={4}>
                            {meetings.map((meeting) => (
                                <Box
                                    key={meeting.id}
                                    bg="white"
                                    border="1px solid #EAECF0"
                                    borderRadius="12px"
                                    p={5}
                                    shadow="sm"
                                    transition="all 0.2s"
                                    _hover={{ shadow: "md", borderColor: "#D0D5DD" }}
                                >
                                    <Flex justify="space-between" align="start" mb={4}>
                                        <Box>
                                            <Flex align="center" gap={3} mb={1}>
                                                <Text fontSize="lg" fontWeight="600" color="#101828">
                                                    {meeting.meetingTitle}
                                                </Text>
                                                {getStatusBadge(meeting.meetingStatusType, meeting.meetingStatusName)}
                                            </Flex>
                                            <Text fontSize="sm" color="gray.500">
                                                Created by {meeting.createdByName}
                                            </Text>
                                        </Box>
                                        <IconButton
                                            aria-label="Edit meeting"
                                            icon={<EditIcon />}
                                            size="sm"
                                            variant="ghost"
                                            color="#667085"
                                            _hover={{ color: "#4048D6", bg: "#F9FAFB" }}
                                            onClick={() => handleEdit(meeting)}
                                        />
                                    </Flex>

                                    <Grid
                                        templateColumns={{
                                            base: "repeat(2,1fr)",
                                            md: "repeat(4,1fr)",
                                        }}
                                        gap={4}
                                        bg={
                                            isMeetingExpired(meeting)
                                                ? "#FEE2E2"
                                                : "#F9FAFB"
                                        }
                                        p={3.5}
                                        borderRadius="8px"
                                        border="1px solid"
                                        borderColor={
                                            isMeetingExpired(meeting)
                                                ? "#FCA5A5"
                                                : "#F2F4F7"
                                        }
                                        className={
                                            isMeetingExpired(meeting)
                                                ? "expired-row"
                                                : isMeetingApproaching(meeting)
                                                    ? "blink-approaching"
                                                    : isMeetingToday(meeting)
                                                        ? "blink-today"
                                                        : ""
                                        }
                                    >

                                        <Box>
                                            <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Meeting Date</Text>
                                            <Text fontWeight="600" color="#344054" fontSize="sm">
                                                {meeting.isAllDay
                                                    ? formatMeetingDate(meeting.meetingDate)
                                                    : showDate(meeting.startDateTime)}
                                            </Text>
                                        </Box>
                                        <Box>
                                            <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Time</Text>
                                            <Text fontWeight="600" color="#344054" fontSize="sm">
                                                {meeting.isAllDay ? "All Day" : `${showTime(meeting.startDateTime)} - ${showTime(meeting.endDateTime)}`}
                                            </Text>
                                        </Box>
                                        <Box>
                                            <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Duration</Text>
                                            <Text fontWeight="600" color="#344054" fontSize="sm">{meeting.meetingDuration} Min</Text>
                                        </Box>
                                        {(meeting.locationName || meeting.locationMaster?.locationName || meeting.locationId) && (
                                            <Box>
                                                <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Location</Text>
                                                <Text fontWeight="600" color="#344054" fontSize="sm">{meeting.locationName || meeting.locationMaster?.locationName || locations.find(l => l.id === meeting.locationId)?.name || "-"}</Text>
                                            </Box>
                                        )}
                                        <Box>
                                            <Text color="#667085" fontSize="12px" fontWeight="500" mb={0.5}>Meeting Link</Text>
                                            {meeting.meetingLinkType === "locationVisit" ? (
                                                <Text fontWeight="600" color="#344054" fontSize="sm">
                                                    📍 {meeting.locationName || meeting.locationMaster?.locationName || locations.find(l => l.id === meeting.locationId)?.name || "Location Visit"}
                                                </Text>
                                            ) : (
                                                <Flex align="center" gap={1}>
                                                    {isValidUrl(meeting.meeting_Link) ? (
                                                        isMeetingLinkActive(meeting) ? (
                                                            <a href={meeting.meeting_Link} target="_blank" rel="noopener noreferrer" style={{ color: "#4048D6", textDecoration: "underline", fontWeight: "600", fontSize: "14px" }}>
                                                                {meeting.meetingLinkType === "googleLink" ? "Google Meet" : "Custom Link"}
                                                            </a>
                                                        ) : (
                                                            <Text color="gray.400" fontWeight="500" cursor="not-allowed" title="Link will be active during the scheduled meeting time" fontSize="sm">
                                                                {meeting.meetingLinkType === "googleLink" ? "Google Meet (Inactive)" : "Custom Link (Inactive)"}
                                                            </Text>
                                                        )
                                                    ) : (
                                                        <Text fontWeight="600" color="#344054" fontSize="sm">
                                                            {meeting.meetingLinkType === "googleLink" ? "Google Meet" : "No Link"}
                                                        </Text>
                                                    )}
                                                    {isValidUrl(meeting.meeting_Link) && (
                                                        <IconButton
                                                            aria-label="Copy link"
                                                            icon={<CopyIcon />}
                                                            size="xs"
                                                            variant="ghost"
                                                            color={isMeetingLinkActive(meeting) ? "#667085" : "gray.300"}
                                                            isDisabled={!isMeetingLinkActive(meeting)}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isMeetingLinkActive(meeting)) handleCopyLink(meeting.meeting_Link!);
                                                            }}
                                                            _hover={isMeetingLinkActive(meeting) ? { color: "#4048D6", bg: "#F9FAFB" } : undefined}
                                                        />
                                                    )}
                                                </Flex>
                                            )}
                                        </Box>
                                    </Grid>

                                    {meeting.description && (
                                        <Box mt={4} pt={3.5} borderTop="1px solid #EAECF0">
                                            <Text color="#344054" fontSize="13px" fontWeight="500" mb={1}>Description</Text>
                                            <Text fontSize="13px" color="#475467" style={{ whiteSpace: "pre-line" }}>{meeting.description}</Text>
                                        </Box>
                                    )}

                                    {meeting.remarks && (
                                        <Box mt={3} pt={3} borderTop="1px dashed #EAECF0">
                                            <Text color="#344054" fontSize="13px" fontWeight="500" mb={1}>Remarks</Text>
                                            <Text fontSize="13px" color="#475467" style={{ whiteSpace: "pre-line" }}>{meeting.remarks}</Text>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Box>
        </MyDiv>
    );
};

export default MeetingFormTabV2;
